import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatAutocomplete,
  MatOption,
} from '@angular/material/autocomplete';
import { MatDialogRef, MatDialogClose } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { AxiosError } from 'axios';
import { partyType } from 'src/app/components/dashboard/enums/enum-descriptors';
import {
  ClientDataTableItem,
  ClientsService,
} from 'src/app/services/clients/clients.service';
import {
  CountriesService,
  Country,
} from 'src/app/services/countries/countries.service';
import { EnumsService } from 'src/app/services/enums/enums.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import {
  CompanySearchAutocompleteItem,
  CompanySearchService,
} from 'src/app/shared/company-search-service/company-search.service';
import { PartyType } from 'src/types';
import { PayeePopupComponent } from '../../../../shared/payee-popup/payee-popup.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeAutocompleteComponent } from '../../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';

@Component({
  selector: 'app-create-client-dialog',
  templateUrl: './create-client-dialog.component.html',
  styleUrls: ['./create-client-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    PayeeAutocompleteComponent,
    PayeeButtonComponent,
    MatDialogClose,
  ],
})
export class CreateClientDialogComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private snackbar = inject(MatSnackBar);
  private clientsService = inject(ClientsService);
  private enumsService = inject(EnumsService);
  private helpersService = inject(HelpersService);
  private companySearchService = inject(CompanySearchService);
  private countriesService = inject(CountriesService);
  private partyTypes: PartyType[] = [];
  private countries: Country[] = [];
  private readonly destroy = new Subject<void>();
  private readonly companySearch$ = new Subject<string>();

  dialogRef =
    inject<
      MatDialogRef<CreateClientDialogComponent, ClientDataTableItem | undefined>
    >(MatDialogRef);

  readonly formGroup = this.fb.group({
    tax_number: this.fb.control('', { nonNullable: true }),
    name: this.fb.control('', {
      validators: Validators.required,
      nonNullable: true,
    }),
    party_type_id: this.fb.control<number | null>(null),
    address: this.fb.group({
      country: this.fb.control('HU', {
        validators: Validators.required,
        nonNullable: true,
      }),
      settlement: this.fb.control<string | null>(null),
      postcode: this.fb.control<string | null>(null),
      street: this.fb.control<string | null>(null),
    }),
  });

  autocompleteOptions: CompanySearchAutocompleteItem[] = [];

  submitLoading = false;
  readonly initLoading = signal(false);

  partyTypeOptions: number[] = [];

  countryOptions: string[] = [];

  get address() {
    return this.formGroup.controls.address;
  }
  get country() {
    return this.address.controls.country;
  }
  get settlement() {
    return this.address.controls.settlement;
  }
  get postcode() {
    return this.address.controls.postcode;
  }
  get street() {
    return this.address.controls.street;
  }
  get tax_number() {
    return this.formGroup.controls.tax_number;
  }
  get name() {
    return this.formGroup.controls.name;
  }
  get party_type_id() {
    return this.formGroup.controls.party_type_id;
  }

  async ngOnInit(): Promise<void> {
    this.companySearch$
      .pipe(takeUntil(this.destroy), debounceTime(500))
      .subscribe(async (name) => {
        try {
          this.autocompleteOptions =
            await this.companySearchService.getCompanySearchAutocompleteItems(
              name,
            );
        } catch (error) {
          console.error('Error while searching company', error);
        }
      });

    try {
      this.initLoading.set(true);

      const [partyTypes, countries] = await Promise.all([
        this.enumsService.getEnum<PartyType>(partyType.model),
        this.countriesService.getCountries(),
      ]);

      this.partyTypes = partyTypes;
      this.partyTypeOptions = partyTypes.map((pt) => pt.id);

      this.countries = countries.countries;
      this.countryOptions = this.countries.map((c) => c.iso);
    } catch (error) {
      console.error(error);
      this.snackbar.open('Valami hiba történt a dialog megnyitásakor', 'OK', {
        duration: 5000,
      });
      this.dialogRef.close();
    } finally {
      this.initLoading.set(false);
    }
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.companySearch$.complete();
  }

  onCompanySearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.companySearch$.next(value);
  }

  companySelected(event: MatAutocompleteSelectedEvent): void {
    const item = event.option.value as CompanySearchAutocompleteItem;
    this.formGroup.patchValue({
      name: item.name,
      address: {
        country: item.country?.iso ?? 'HU',
        postcode: item.postcode,
        settlement: item.settlement,
        street: item.street,
      },
      tax_number: item.tax_number,
      party_type_id: item.party_type?.id,
    });
  }

  async submit() {
    if (this.formGroup.invalid || this.submitLoading) {
      console.warn('Submit not available', {
        isFormValid: this.formGroup.valid,
        isSubmitLoading: this.submitLoading,
      });
      return;
    }

    try {
      this.submitLoading = true;

      const value = this.formGroup.getRawValue();

      const result = await this.clientsService.createClient({
        address: {
          country: value.address.country,
          postcode: value.address.postcode!,
          settlement: value.address.settlement!,
          street: value.address.street!,
        },
        name: value.name,
        tax_number: value.tax_number,
        party_type_id: value.party_type_id!,
      });
      this.dialogRef.close(result);
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 422) {
          const errors: Record<string, string[]> = error.response.data.errors;
          const errorCode = error.response.data.code;

          // Check for unique constraint violation using the code field
          if (errorCode === 'not-unique') {
            this.snackbar.open(
              'Ez az adószám már szerepel a rendszerben!',
              'OK',
              {
                duration: 5000,
              },
            );
            this.tax_number.setErrors({ unique: true });
            return;
          }

          this.helpersService.markAllChildrenAsDirty(this.formGroup);
          Object.entries(errors).forEach(([key, values]) => {
            const control = this.formGroup.get(key);
            if (control) {
              control.setErrors({
                backend: values[0],
              });
            } else {
              this.formGroup.setErrors({
                backend: values[0],
              });
            }
          });
          return;
        }
      }
      console.error('Error while creating client', error);
      this.snackbar.open('Valami hiba történt mentéskor!', 'OK', {
        duration: 5000,
      });
      this.dialogRef.close();
    } finally {
      this.submitLoading = false;
    }
  }

  readonly partyTypeDisplayWith = (id: number): string => {
    return this.partyTypes.find((t) => t.id === id)?.label || '';
  };

  readonly countryDisplayWith = (iso: string): string => {
    return this.countries.find((c) => c.iso === iso)?.name || '';
  };
}
